import React from 'react'
import theme from '@ifca-root/react-component/src/assets/theme'
import {
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
} from '@material-ui/core'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import BusinessIcon from '@material-ui/icons/Business'
import TrendingUpIcon from '@material-ui/icons/TrendingUp'
import TrendingDownIcon from '@material-ui/icons/TrendingDown'
import MoreVert from '@material-ui/icons/MoreVert'
import AddIcon from '@material-ui/icons/Add'
import EmptyList from '@ifca-root/react-component/src/components/CardList/EmptyList'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import { useMenuOption } from 'helpers/Hooks/useMenuOption'

interface DepartmentAllocation {
  department_id: string
  department_name: string
  department_code: string
  location_id: string
  location_name: string
  old_percentage: number
  new_percentage: number
  old_quantity: number
  new_quantity: number
  source_document_id: string
  source_document_type: string
}

interface DepartmentApportionmentGridProps {
  allocations: DepartmentAllocation[]
  onAddClick: () => void
  onEditClick: (index: number, allocation: DepartmentAllocation) => void
  onDeleteClick: (index: number) => void
  getTotalNewPercentage: () => number
  validationErrors: string[]
  validationWarnings: string[]
  disabled?: boolean
}

export const DepartmentApportionmentGrid: React.FC<DepartmentApportionmentGridProps> = ({
  allocations,
  onAddClick,
  onEditClick,
  onDeleteClick,
  getTotalNewPercentage,
  validationErrors,
  validationWarnings,
  disabled = false,
}) => {
  const {
    anchorEl,
    setAnchorEl,
    menu,
    handleClick,
    handleClose,
  } = useMenuOption()

  const getChangeColor = (oldVal: number, newVal: number) => {
    if (newVal > oldVal) return 'green'
    if (newVal < oldVal) return 'red'
    return '#666'
  }

  const getChangeIcon = (oldVal: number, newVal: number) => {
    if (newVal > oldVal)
      return <TrendingUpIcon style={{ fontSize: 16, color: 'green' }} />
    if (newVal < oldVal)
      return <TrendingDownIcon style={{ fontSize: 16, color: 'red' }} />
    return null
  }

  const totalPercentage = getTotalNewPercentage()
  const isValidTotal = Math.abs(totalPercentage - 100) <= 0.01

  return (
    <>
      <CardContents
        section={{
          header: {
            title: 'Department Apportionment (%)',
            onClickAction: !disabled ? onAddClick : undefined,
            icon: !disabled ? (
              <AddIcon
                htmlColor="white"
                fontSize="default"
                style={{
                  width: '1.3rem',
                  height: '1.3rem',
                  margin: '0',
                  background: theme.palette.primary.main,
                  boxShadow: `1px 1px 4px 0px ${theme.palette.primary.main}`,
                  borderRadius: '3px',
                  color: 'rgba(224,234,254,100)',
                  marginTop: '10px',
                }}
              />
            ) : null,
          },
        }}
      >
        {allocations.length === 0 ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
              width: '100%',
            }}
          >
            <EmptyList
              title="No Allocations"
              subtitle={
                disabled
                  ? 'Select an asset to view department allocations'
                  : 'Click the + button to add department allocation'
              }
            />
          </div>
        ) : (
          <>
            <div className="rm-padding table-wrap">
              {allocations.map((alloc, index) => {
                const percentageChange =
                  alloc.new_percentage - alloc.old_percentage
                const hasChange = Math.abs(percentageChange) > 0.01

                return (
                  <React.Fragment key={index}>
                    <Grid spacing={1} container className="table-content">
                      <Grid item xs={1} style={{ placeSelf: 'start' }}>
                        <span className="xxTitle">{`${index + 1}.`}</span>
                      </Grid>

                      <Grid
                        item
                        xs={7}
                        style={{
                          placeSelf: 'start',
                          marginTop: '8px',
                        }}
                      >
                        {/* Department */}
                        <div
                          className="xsTitle text-noflow"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <BusinessIcon
                            style={{
                              fontSize: 16,
                              marginRight: 6,
                              color: theme.palette.primary.main,
                            }}
                          />
                          {alloc.department_code || alloc.department_name}
                        </div>

                        {/* Department Name */}
                        {alloc.department_code && alloc.department_name && (
                          <div
                            className="desc text-noflow"
                            style={{ marginLeft: '22px' }}
                          >
                            {alloc.department_name}
                          </div>
                        )}

                        {/* Location */}
                        {alloc.location_name && (
                          <div
                            className="desc text-noflow"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginTop: '4px',
                            }}
                          >
                            <LocationOnIcon
                              style={{
                                fontSize: 14,
                                marginRight: 6,
                                color: '#F39C12',
                              }}
                            />
                            {alloc.location_name}
                          </div>
                        )}

                        {/* Percentage Info */}
                        <div style={{ marginTop: '8px', marginLeft: '22px' }}>
                          <Grid container spacing={1}>
                            <Grid item xs={4}>
                              <div style={{ fontSize: '10px', color: '#999' }}>
                                {`Alloc. (%)`}
                              </div>
                              <div
                                style={{ fontSize: '12px', fontWeight: 500 }}
                              >
                                {alloc.old_percentage.toFixed(2)}%
                              </div>
                            </Grid>
                            <Grid item xs={4}>
                              <div style={{ fontSize: '10px', color: '#999' }}>
                                {`Changed Alloc. (%)`}
                              </div>
                              <div
                                style={{
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  color: hasChange
                                    ? getChangeColor(
                                        alloc.old_percentage,
                                        alloc.new_percentage
                                      )
                                    : '#000',
                                }}
                              >
                                {alloc.new_percentage.toFixed(2)}%
                              </div>
                            </Grid>
                            <Grid item xs={4}>
                              <div style={{ fontSize: '10px', color: '#999' }}>
                                Change
                              </div>
                              <div
                                style={{
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  color: getChangeColor(
                                    alloc.old_percentage,
                                    alloc.new_percentage
                                  ),
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                {percentageChange > 0 ? '+' : ''}
                                {percentageChange.toFixed(2)}%
                                {hasChange && (
                                  <span style={{ marginLeft: '4px' }}>
                                    {getChangeIcon(
                                      alloc.old_percentage,
                                      alloc.new_percentage
                                    )}
                                  </span>
                                )}
                              </div>
                            </Grid>
                          </Grid>
                        </div>
                      </Grid>

                      <Grid
                        item
                        xs={3}
                        style={{
                          placeSelf: 'start',
                          marginTop: '8px',
                          textAlign: 'right',
                        }}
                      >
                        <div style={{ fontSize: '10px', color: '#999' }}>
                          Qty
                        </div>
                        <div className="xxTitle">
                          {alloc.new_quantity?.toFixed(2) || '0.00'}
                        </div>
                      </Grid>

                      <Grid item xs={1} style={{ marginTop: '0px' }}>
                        {!disabled && (
                          <IconButton
                            edge="end"
                            aria-label="options"
                            aria-controls="apportionment-menu"
                            aria-haspopup="true"
                            onClick={e =>
                              handleClick(e, alloc.department_id, index, alloc)
                            }
                          >
                            <MoreVert />
                          </IconButton>
                        )}
                      </Grid>
                    </Grid>

                    <Divider
                      variant="fullWidth"
                      style={{ background: '#E4E4E4' }}
                    />
                  </React.Fragment>
                )
              })}
            </div>
          </>
        )}
      </CardContents>

      {/* Context Menu */}
      <Menu
        id="apportionment-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem
          onClick={() => {
            if (menu?.index !== undefined && menu?.obj) {
              onEditClick(menu.index, menu.obj)
            }
            handleClose()
          }}
        >
          <span className="mdDesc">Edit</span>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menu?.index !== undefined) {
              onDeleteClick(menu.index)
            }
            handleClose()
          }}
        >
          <span className="mdDesc">Delete</span>
        </MenuItem>
      </Menu>
    </>
  )
}
